import type { EarnedAchievement } from '../../api/models';
import { ChallengeShell } from '../../challenges/ChallengeShell';
import { AppText } from '../../components/AppText';
import { InlineNotice } from '../../components/InlineNotice';
import { PrimaryButton } from '../../components/PrimaryButton';
import { SecondaryButton } from '../../components/SecondaryButton';
import { currentTask, position } from '../../session/sessionState';
import { Strings } from '../../strings';
import { SymbolView } from '../../symbols/SymbolView';

import { useSession } from './useSession';

/**
 * The training session — one task at a time, then the reward panel.
 * Mirrors android/…/Screens/Session/SessionScreen.kt.
 *
 * design-system/08-Screens.md §3 and §4.
 */
export function SessionScreen({ onLeave }: { onLeave: () => void }) {
  const model = useSession();
  const state = model.state;
  const session = state.session;

  if (state.isLoading) return <Loading />;

  if (state.error !== null) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {/* `info`, never `error` — there is no red on a child's screen. */}
        <InlineNotice text={state.error} tone="info" />
        <PrimaryButton label={Strings.actionRetry} onClick={() => void model.start()} />
        {/*
          A way out, not only a way to try again. Retrying a failure whose cause has not changed just
          fails again, and a child left with one button that does not work is stuck (R0069).
        */}
        <SecondaryButton label={Strings.childNavHome} onClick={onLeave} />
      </div>
    );
  }

  if (session === null) return <Loading />;

  const task = currentTask(session);

  if (state.isFinished || session.isComplete || task === null) {
    return (
      <RewardPanel
        message={session.completionMessage ?? session.rewardMessage ?? ''}
        stars={session.starsEarned}
        coins={session.coinsEarned}
        earnedAchievements={session.earnedAchievements}
        onLeave={onLeave}
      />
    );
  }

  return (
    <ChallengeShell
      task={task}
      position={position(session)}
      total={session.plannedCount}
      isSubmitting={state.isSubmitting}
      onSelect={model.select}
      onType={model.type}
      onReorder={model.reorder}
      hint={state.hint}
      hintsRemaining={state.hintsRemaining}
      submitError={state.submitError}
      onSubmit={() => void model.submit()}
      onRequestHint={() => void model.requestHint()}
      onNext={model.next}
    />
  );
}

function Loading() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
      <AppText style="titleM" color="var(--text-secondary)">
        {Strings.childHomeToday}
      </AppText>
    </div>
  );
}

/**
 * The end of a session — design-system/08-Screens.md §4.
 *
 * Every word here comes from the API. The client arranges stars and coins around a sentence it did not
 * write, because the vocabulary rules apply hardest at exactly the moment a child is being told how they
 * did (BR-13).
 */
function RewardPanel({
  message,
  stars,
  coins,
  earnedAchievements,
  onLeave,
}: {
  message: string;
  stars: number;
  coins: number;
  earnedAchievements: EarnedAchievement[];
  onLeave: () => void;
}) {
  return (
    <div
      style={{
        minHeight: '70vh',
        background: 'var(--surface-reward)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-6)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-6)',
      }}
    >
      <SymbolView token="star" size={96} color="var(--reward-star)" />

      {message.trim().length > 0 ? (
        <AppText style="titleM" align="center">
          {message}
        </AppText>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--space-5)' }}>
        <RewardCount label={Strings.childStars} value={stars} color="var(--reward-star)" />
        <RewardCount label={Strings.childCoins} value={coins} color="var(--reward-coin)" />
      </div>

      {/*
        The moment that actually motivates. An achievement earned is announced here, once, at the moment it
        happened — the screen that lists them is only where it is remembered afterwards.

        Its name and description are the API's, like every other word on this panel. Empty is the common
        case: most sessions cross no threshold, and nothing is shown then rather than a consolation.
      */}
      {earnedAchievements.map((achievement: EarnedAchievement) => (
        <div
          key={achievement.code}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            borderRadius: 'var(--radius-l)',
            background: 'var(--surface-raised)',
            padding: 'var(--space-4)',
          }}
        >
          <SymbolView token={achievement.symbol} size={40} color="var(--action-primary)" />

          <div style={{ flex: 1 }}>
            <AppText style="label" color="var(--action-primary)">
              {Strings.achievementsNew}
            </AppText>
            <AppText style="titleS">{achievement.name}</AppText>
            <AppText style="bodyS" color="var(--text-secondary)">
              {achievement.description}
            </AppText>
          </div>
        </div>
      ))}

      <PrimaryButton label={Strings.actionContinue} onClick={onLeave} />
    </div>
  );
}

function RewardCount({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-m)',
        background: 'var(--surface-raised)',
        padding: 'var(--space-4) var(--space-5)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <AppText style="titleL" color={color}>
        {value}
      </AppText>
      <AppText style="bodyS" color="var(--text-secondary)">
        {label}
      </AppText>
    </div>
  );
}
